import React from 'react';
import PropTypes from 'prop-types';

import useComponentDidMount from 'Hooks/useComponentDidMount';
import fr from 'facet-rest';
import { useSurveyContext } from 'Views/Onboarding/Pages/Survey/SurveyContext';

const DetailListContext = React.createContext(undefined);

// Actions
export const FETCH_NEW_ITEM_COLLECTION_STARTED = 'fetch-new-item-collection-started';
export const FETCH_NEW_ITEM_COLLECTION_SUCCEEDED = 'fetch-new-item-collection-succeeded';
export const FETCH_NEW_ITEM_COLLECTION_FAILED = 'fetch-new-item-collection-failed';

export const FETCH_DETAIL_RESOURCE_STARTED = 'fetch-detail-resource-started';
export const FETCH_DETAIL_RESOURCE_FAILED = 'fetch-detail-resource-failed';

const init = () => {
  return {
    newItemCollection: null,
    isFetchingNewItemCollection: false,
    fetchingNewItemCollectionError: null,
    isFetchingDetailResource: false,
    fetchingDetailResourceError: null,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case FETCH_NEW_ITEM_COLLECTION_STARTED: {
      return {
        ...state,
        isFetchingNewItemCollection: true,
        fetchingNewItemCollectionError: null,
      };
    }
    case FETCH_NEW_ITEM_COLLECTION_SUCCEEDED: {
      return {
        ...state,
        isFetchingNewItemCollection: false,
        fetchingNewItemCollectionError: null,
        newItemCollection: action.newItemCollection,
      };
    }
    case FETCH_NEW_ITEM_COLLECTION_FAILED: {
      return {
        ...state,
        isFetchingNewItemCollection: false,
        fetchingNewItemCollectionError: action.fetchingNewItemCollectionError,
      };
    }
    case FETCH_DETAIL_RESOURCE_STARTED: {
      return {
        ...state,
        isFetchingDetailResource: true,
        fetchingDetailResourceError: null,
      };
    }
    case FETCH_DETAIL_RESOURCE_FAILED: {
      return {
        ...state,
        isFetchingDetailResource: false,
        fetchingDetailResourceError: action.fetchingDetailResourceError,
      };
    }
    default: {
      throw new Error(`Unhandled DetailListContext action type: ${action.type}`);
    }
  }
};

const DetailListProvider = ({
  children,
}) => {
  const {
    onboardingStepResource,
    setOnboardingStepCollection,
  } = useSurveyContext();
  const [state, dispatch] = React.useReducer(reducer, null, init);

  const handleFetchDetailResource = React.useCallback(async (detailResource) => {
    try {
      dispatch({ type: FETCH_DETAIL_RESOURCE_STARTED });

      const coll = await detailResource.link('next').get();

      setOnboardingStepCollection(coll);
    } catch (err) {
      dispatch({ type: FETCH_DETAIL_RESOURCE_FAILED, fetchingDetailResourceError: err });
    }
  }, [setOnboardingStepCollection]);

  const handleClickAddNewItem = React.useCallback(async () => {
    setOnboardingStepCollection(state.newItemCollection);
  }, [state]);

  const memoizedValue = React.useMemo(() => {
    return {
      newItemCollection: state.newItemCollection,
      isFetchingNewItemCollection: state.isFetchingNewItemCollection,
      fetchingNewItemCollectionError: state.fetchingNewItemCollectionError,
      isFetchingDetailResource: state.isFetchingDetailResource,
      fetchingDetailResourceError: state.fetchingDetailResourceError,
      handleFetchDetailResource,
      handleClickAddNewItem,
    };
  }, [
    state,
    handleFetchDetailResource,
  ]);

  useComponentDidMount(async () => {
    try {
      dispatch({ type: FETCH_NEW_ITEM_COLLECTION_STARTED });

      const link = onboardingStepResource.relationship(fr.relationships.newItem.name)?.link('related');
      if (!link) {
        dispatch({ type: FETCH_NEW_ITEM_COLLECTION_SUCCEEDED, newItemCollection: null });
      } else {
        const coll = await link.get();

        dispatch({ type: FETCH_NEW_ITEM_COLLECTION_SUCCEEDED, newItemCollection: coll });
      }
    } catch (err) {
      dispatch({ type: FETCH_NEW_ITEM_COLLECTION_FAILED, fetchingNewItemCollectionError: err });
    }
  });

  return (
    <DetailListContext.Provider value={memoizedValue}>
      {children}
    </DetailListContext.Provider>
  );
};

// Use Survey State Context
const useDetailListContext = () => {
  const context = React.useContext(DetailListContext);
  if (context === undefined) {
    throw new Error('useDetailListContext must be used within a DetailListProvider');
  }
  return context;
};

DetailListProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export {
  DetailListProvider,
  useDetailListContext,
};
